import React, { useEffect, useMemo, useState } from "react";
import { ClipboardText } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { DataTable } from "@/components/DataTable";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const GROUP_LABELS = {
  "sos.": "SOS & Safety", "instructor.": "Instructors", "attribution.": "Attributions",
  "payouts.": "Payouts", "class.": "Classes", "school.": "Schools", "grace.": "Grace periods",
  "mfa.": "Security", "refund.": "Refunds", "user.": "Users", "pillar.": "Pillars",
  "matrix.": "Lesson Matrix", "renewal.": "Renewals", "profit_share.": "Profit share",
  "student.": "Students",
};

const detailText = (d) => {
  if (!d) return "—";
  if (typeof d === "string") return d;
  return Object.entries(d).map(([k, v]) =>
    `${k}: ${typeof v === "object" && v !== null ? JSON.stringify(v) : v}`).join(" · ");
};

export default function AuditLogPanel() {
  const [data, setData] = useState(null);
  const [group, setGroup] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = () => {
    const params = {};
    if (group !== "all") params.action = group;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    params.limit = 300;
    api.get("/admin/audit-logs", { params })
      .then(r => setData(r.data))
      .catch(e => toast.error(formatApiError(e)));
  };
  useEffect(load, [group, dateFrom, dateTo]); // eslint-disable-line

  const groups = useMemo(() => {
    const present = new Set((data?.actions || []).map(a => a.split(".")[0] + "."));
    return Object.entries(GROUP_LABELS).filter(([p]) => present.has(p));
  }, [data?.actions]);

  const rows = useMemo(() => (data?.logs || []).map(l => ({
    id: l.id, at: l.at, action: l.action,
    actor: l.actor_name, actor_role: l.actor_role,
    detail: detailText(l.detail),
  })), [data?.logs]);

  return (
    <div className="rounded-2xl border border-[#C4C8CE] bg-white p-5" data-testid="audit-log-panel">
      <h3 className="font-display font-bold text-lg flex items-center gap-2">
        <ClipboardText size={20} weight="duotone" /> Audit Log
        {data && <span className="text-xs font-normal text-[#000000]">({data.total} total entries)</span>}
      </h3>
      <p className="text-xs text-[#000000] mb-3">Every sensitive action — refunds, certifications, role changes, SOS events, payouts, deletions — with actor and timestamp.</p>

      <div className="flex flex-wrap items-end gap-2 mb-3">
        <Select value={group} onValueChange={setGroup}>
          <SelectTrigger data-testid="audit-group-select" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            {groups.map(([prefix, label]) => <SelectItem key={prefix} value={prefix}>{label}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="text-[10px] font-semibold text-[#000000]">From
          <input data-testid="audit-date-from" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="block rounded-xl border border-[#C4C8CE] p-1.5 text-xs mt-0.5" /></label>
        <label className="text-[10px] font-semibold text-[#000000]">To
          <input data-testid="audit-date-to" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="block rounded-xl border border-[#C4C8CE] p-1.5 text-xs mt-0.5" /></label>
      </div>

      <DataTable
        tableId="audit-log"
        defaultSort={[{ key: "at", dir: "desc" }]}
        rowTestId={(r) => `audit-row-${r.id}`}
        columns={[
          { key: "at", label: "When", type: "date", render: r => <span className="whitespace-nowrap text-xs">{r.at?.replace("T", " ").slice(0, 19)}</span> },
          { key: "action", label: "Action", render: r => <span className="font-mono text-xs font-bold">{r.action}</span> },
          { key: "actor", label: "Actor", render: r => <span>{r.actor}{r.actor_role ? <span className="text-[10px] text-[#000000]"> · {r.actor_role}</span> : null}</span> },
          { key: "detail", label: "Detail", sortable: false, render: r => <span className="text-xs text-[#000000] break-all">{r.detail}</span> },
        ]}
        rows={rows}
        emptyText="No audit entries match these filters."
      />
    </div>
  );
}
